import { EditorView } from 'prosemirror-view';
import { EditorState, Selection, TextSelection } from 'prosemirror-state';
import { toggleMark, setBlockType } from 'prosemirror-commands';
import { MarkType, NodeType } from 'prosemirror-model';
import { wrapInList, liftListItem } from 'prosemirror-schema-list';
import {
  addRowBefore,
  addRowAfter,
  deleteRow,
  addColumnBefore,
  addColumnAfter,
  deleteColumn,
  mergeCells,
  splitCell,
  toggleHeaderRow,
  deleteTable,
  cellAround,
  CellSelection,
} from 'prosemirror-tables';
import { schema } from './schema';
import { findMultiplierFromDoc } from './plugins/multiplier-plugin';
import { YjsSync } from './sync/yjs-sync';
import { isLockedMode, toggleLockedMode } from './plugins/locked-mode-plugin';
import { TASK_TOGGLE_META } from './plugins/task-plugin';
import qrcode from 'qrcode-generator';

interface ToolbarButton {
  className: string;
  title: string;
  command: (view: EditorView) => boolean;
  isActive?: (view: EditorView) => boolean;
  isDisabled?: (view: EditorView) => boolean;
}

function markActive(view: EditorView, markType: typeof schema.marks.strong | typeof schema.marks.em | typeof schema.marks.tag): boolean {
  const { from, $from, to, empty } = view.state.selection;
  if (empty) {
    return !!markType.isInSet(view.state.storedMarks || $from.marks());
  }
  return view.state.doc.rangeHasMark(from, to, markType);
}

function blockTypeActive(view: EditorView, nodeType: typeof schema.nodes.heading, attrs?: { level: number }): boolean {
  const { $from } = view.state.selection;
  const parent = $from.parent;
  if (parent.type !== nodeType) return false;
  if (attrs && parent.attrs.level !== attrs.level) return false;
  return true;
}

function inHeading(view: EditorView): boolean {
  return view.state.selection.$from.parent.type === schema.nodes.heading;
}

function inTask(view: EditorView): boolean {
  return view.state.selection.$from.parent.type === schema.nodes.task;
}

function inTaskOrH1(view: EditorView): boolean {
  const parent = view.state.selection.$from.parent;
  return parent.type === schema.nodes.task ||
    (parent.type === schema.nodes.heading && parent.attrs.level === 1);
}

function inListType(view: EditorView, listType: NodeType): boolean {
  const { $from } = view.state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type === listType) return true;
  }
  return false;
}

function inTableCell(view: EditorView): boolean {
  const selection = view.state.selection;
  // CellSelection (multiple cells selected) - show toolbar
  if (selection instanceof CellSelection) return true;
  // Only show for collapsed cursor inside a cell, not text selections
  if (!selection.empty) return false;
  return cellAround(selection.$from) !== null;
}

export function insertTableCommand(state: EditorState, dispatch?: EditorView['dispatch']): boolean {
  const { $from } = state.selection;
  const parent = $from.parent;

  const para = schema.nodes.paragraph.create();
  const headerCell = schema.nodes.table_header.create(null, para);
  const headerRow = schema.nodes.table_row.create(null, [headerCell, headerCell.copy(headerCell.content)]);
  const cell = schema.nodes.table_cell.create(null, para);
  const bodyRow = schema.nodes.table_row.create(null, [cell, cell.copy(cell.content)]);
  const table = schema.nodes.table.create(null, [headerRow, bodyRow]);

  // Replace empty paragraph, otherwise insert after current block
  const isEmptyParagraph = parent.type === schema.nodes.paragraph && parent.content.size === 0;
  const insertPos = isEmptyParagraph ? $from.before() : $from.after($from.depth);
  const tr = isEmptyParagraph
    ? state.tr.replaceWith($from.before(), $from.after(), table)
    : state.tr.insert(insertPos, table);

  // Place cursor in first cell (table +1, row +1, cell +1, para +1 = +4)
  tr.setSelection(TextSelection.create(tr.doc, insertPos + 4)).scrollIntoView();
  if (dispatch) dispatch(tr);
  return true;
}

function insertTable(view: EditorView): boolean {
  const result = insertTableCommand(view.state, view.dispatch);
  view.focus();
  return result;
}

function toggleList(listType: NodeType): (state: EditorState, dispatch?: EditorView['dispatch']) => boolean {
  return (state, dispatch) => {
    // wrapInList returns false if already in a list of this type
    if (wrapInList(listType)(state)) {
      return wrapInList(listType)(state, dispatch);
    }
    // Already in list, try to lift out
    return liftListItem(schema.nodes.listItem)(state, dispatch);
  };
}

/**
 * Toggle block type command - if already the target type, convert to paragraph.
 */
function toggleBlockType(
  nodeType: typeof schema.nodes.heading,
  attrs: { level: number }
): (state: EditorState, dispatch?: EditorView['dispatch']) => boolean {
  return (state, dispatch) => {
    const { $from } = state.selection;
    const isActive = $from.parent.type === nodeType && $from.parent.attrs.level === attrs.level;

    if (isActive) {
      return setBlockType(schema.nodes.paragraph)(state, dispatch);
    } else {
      return setBlockType(nodeType, attrs)(state, dispatch);
    }
  };
}

/**
 * Toggle mark command that removes from marked range when cursor is inside.
 * Adding marks requires a selection.
 */
function toggleMarkCommand(view: EditorView, markType: MarkType): boolean {
  const { $from, empty } = view.state.selection;

  if (empty && markType.isInSet($from.marks())) {
    const doc = view.state.doc;
    let start = $from.pos, end = $from.pos;

    // Expand to cover the full contiguous marked range
    while (start > $from.start() && markType.isInSet(doc.resolve(start).marks())) start--;
    while (end < $from.end() && markType.isInSet(doc.resolve(end).marks())) end++;

    view.dispatch(view.state.tr.removeMark(start, end, markType));
    return true;
  }

  return toggleMark(markType)(view.state, view.dispatch);
}

const buttons: ToolbarButton[] = [
  {
    className: 'bold-btn',
    title: 'Bold (Ctrl+B)',
    command: (view) => {
      toggleMarkCommand(view, schema.marks.strong);
      view.focus();
      return true;
    },
    isActive: (view) => markActive(view, schema.marks.strong),
    isDisabled: inHeading,
  },
  {
    className: 'italic-btn',
    title: 'Italic (Ctrl+I)',
    command: (view) => {
      toggleMarkCommand(view, schema.marks.em);
      view.focus();
      return true;
    },
    isActive: (view) => markActive(view, schema.marks.em),
    isDisabled: inHeading,
  },
  {
    className: 'h2-btn',
    title: 'Heading 2 (Ctrl+2)',
    command: (view) => {
      toggleBlockType(schema.nodes.heading, { level: 2 })(view.state, view.dispatch);
      view.focus();
      return true;
    },
    isActive: (view) => blockTypeActive(view, schema.nodes.heading, { level: 2 }),
    isDisabled: inTaskOrH1,
  },
  {
    className: 'h3-btn',
    title: 'Heading 3 (Ctrl+3)',
    command: (view) => {
      toggleBlockType(schema.nodes.heading, { level: 3 })(view.state, view.dispatch);
      view.focus();
      return true;
    },
    isActive: (view) => blockTypeActive(view, schema.nodes.heading, { level: 3 }),
    isDisabled: inTaskOrH1,
  },
  {
    className: 'paragraph-btn',
    title: 'Paragraph (Ctrl+0)',
    command: (view) => {
      setBlockType(schema.nodes.paragraph)(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
  {
    className: 'bullet-list-btn',
    title: 'Bullet List',
    command: (view) => {
      toggleList(schema.nodes.bulletList)(view.state, view.dispatch);
      view.focus();
      return true;
    },
    isActive: (view) => inListType(view, schema.nodes.bulletList),
    isDisabled: inHeading,
  },
  {
    className: 'ordered-list-btn',
    title: 'Numbered List',
    command: (view) => {
      toggleList(schema.nodes.orderedList)(view.state, view.dispatch);
      view.focus();
      return true;
    },
    isActive: (view) => inListType(view, schema.nodes.orderedList),
    isDisabled: inHeading,
  },
  {
    className: 'table-btn',
    title: 'Insert 2x2 table (Ctrl+Shift+T)',
    command: (view) => {
      insertTable(view);
      return true;
    },
  },
  {
    className: 'task-btn',
    title: 'Task',
    command: (view) => {
      const { $from } = view.state.selection;
      const tr = view.state.tr.setNodeMarkup($from.before(), schema.nodes.task, {
        done: false,
        active: false,
      });
      view.dispatch(tr);
      view.focus();
      return true;
    },
  },
  {
    className: 'multiplier-btn',
    title: 'Multiplier (mark selected number as the multiplier)',
    command: (view) => {
      toggleMark(schema.marks.multiplier)(view.state, view.dispatch);
      view.focus();
      return true;
    },
    isDisabled: inHeading,
  },
  {
    className: 'number-btn',
    title: 'Insert number (multiplied by the multiplier)',
    command: (view) => {
      const { from, to } = view.state.selection;
      const selectedText = view.state.doc.textBetween(from, to);
      const parsedNumber = parseFloat(selectedText);
      const multiplier = findMultiplierFromDoc(view);
      const base = !isNaN(parsedNumber) ? parsedNumber / multiplier : 1;

      const node = schema.nodes.numberNode.create({ base });
      const tr = view.state.tr.replaceSelectionWith(node);
      view.dispatch(tr);
      view.focus();
      return true;
    },
    isDisabled: inHeading,
  },
  {
    className: 'tag-btn',
    title: 'Tag',
    command: (view) => {
      toggleMark(schema.marks.tag)(view.state, view.dispatch);
      view.focus();
      return true;
    },
    isActive: (view) => markActive(view, schema.marks.tag),
    isDisabled: inHeading,
  },
  {
    className: 'reset-btn',
    title: 'Reset all tasks (or selected tasks)',
    command: (view) => {
      const { from, to } = view.state.selection;
      const hasSelection = from !== to;

      // Collect positions first, then apply in reverse order to avoid position shifts
      const positions: number[] = [];
      view.state.doc.descendants((node, pos) => {
        if (node.type.name === 'task' && (node.attrs.done || node.attrs.active)) {
          // If there's a selection, only include tasks that overlap with it
          if (hasSelection) {
            const nodeEnd = pos + node.nodeSize;
            if (pos < to && nodeEnd > from) {
              positions.push(pos);
            }
          } else {
            positions.push(pos);
          }
        }
        return true;
      });

      if (positions.length === 0) {
        view.focus();
        return true;
      }

      let tr = view.state.tr;
      // Apply in reverse order to avoid position shifting issues
      for (const pos of positions.reverse()) {
        const node = tr.doc.nodeAt(pos);
        if (node) {
          tr = tr.setNodeMarkup(pos, null, {
            ...node.attrs,
            done: false,
            active: false,
          });
        }
      }

      tr = tr.setMeta(TASK_TOGGLE_META, true);
      view.dispatch(tr);
      view.focus();
      return true;
    },
  },
  {
    className: 'qr-btn',
    title: 'Show QR code for this document',
    command: (view) => {
      showQRCodeModal(view);
      return true;
    },
  },
];

const secondaryButtons: ToolbarButton[] = [];

const tableButtons: ToolbarButton[] = [
  {
    className: 'table-row-insert-above-btn',
    title: 'Insert row above',
    command: (view) => {
      addRowBefore(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
  {
    className: 'table-row-insert-below-btn',
    title: 'Insert row below',
    command: (view) => {
      addRowAfter(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
  {
    className: 'table-row-delete-btn',
    title: 'Delete row',
    command: (view) => {
      deleteRow(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
  {
    className: 'table-column-insert-left-btn',
    title: 'Insert column left',
    command: (view) => {
      addColumnBefore(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
  {
    className: 'table-column-insert-right-btn',
    title: 'Insert column right',
    command: (view) => {
      addColumnAfter(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
  {
    className: 'table-column-delete-btn',
    title: 'Delete column',
    command: (view) => {
      deleteColumn(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
  {
    className: 'table-merge-cells-btn',
    title: 'Merge cells',
    command: (view) => {
      mergeCells(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
  {
    className: 'table-split-cell-btn',
    title: 'Split cell',
    command: (view) => {
      splitCell(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
  {
    className: 'table-toggle-header-btn',
    title: 'Toggle header row',
    command: (view) => {
      toggleHeaderRow(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
  {
    className: 'table-delete-btn',
    title: 'Delete table',
    command: (view) => {
      deleteTable(view.state, view.dispatch);
      view.focus();
      return true;
    },
  },
];

// Presentation button toggles presentation mode (2x scaling)
function createPresentationButton(): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'presentation-button presentation-btn';
  button.title = 'Toggle presentation mode (2x size)';

  button.addEventListener('click', (e) => {
    e.preventDefault();
    const editor = document.getElementById('editor');
    if (editor) {
      editor.classList.toggle('presentation-mode');
      button.classList.toggle('active', editor.classList.contains('presentation-mode'));
    }
  });

  return button;
}

// Lock button is special - it toggles state and updates its appearance
function createLockButton(view: EditorView): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'lock-button lock-btn';

  const updateButton = () => {
    const locked = isLockedMode(view);
    button.title = locked ? 'Unlock editing' : 'Lock editing (tasks only)';
    button.classList.toggle('locked', locked);
  };

  updateButton();

  button.addEventListener('click', (e) => {
    e.preventDefault();
    toggleLockedMode(view);
    updateButton();
  });

  return button;
}

function showQRCodeModal(view: EditorView): void {
  // Remove existing modal if any
  const existing = document.getElementById('qr-modal');
  if (existing) {
    existing.remove();
  }

  const modal = document.createElement('div');
  modal.id = 'qr-modal';
  modal.className = 'qr-modal';

  const closeModal = () => {
    modal.remove();
    document.removeEventListener('keydown', handleKeydown);
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeModal();
    }
  };

  modal.addEventListener('click', closeModal);
  document.addEventListener('keydown', handleKeydown);

  const content = document.createElement('div');
  content.className = 'qr-modal-content';

  // Build URL with locked parameter if currently locked
  const baseUrl = window.location.origin + window.location.pathname;
  const url = isLockedMode(view) ? `${baseUrl}?locked` : baseUrl;

  const qr = qrcode(0, 'M');
  qr.addData(url);
  qr.make();

  const qrContainer = document.createElement('div');
  qrContainer.className = 'qr-modal-qr';
  qrContainer.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 4 });

  const urlText = document.createElement('p');
  urlText.className = 'qr-modal-url';
  urlText.textContent = url;

  content.appendChild(qrContainer);
  content.appendChild(urlText);
  modal.appendChild(content);
  document.body.appendChild(modal);
}

export interface ToolbarInstance {
  element: HTMLElement;
  secondaryToolbar: HTMLElement;
  update: () => void;
}

export function createToolbar(view: EditorView, sync: YjsSync): ToolbarInstance {
  const toolbar = document.createElement('div');
  toolbar.className = 'toolbar';

  // Track if mousedown started inside a table cell (to prevent toolbar showing on drag-across)
  let mouseDownInTable = true;
  view.dom.addEventListener('mousedown', (e) => {
    const target = e.target as HTMLElement;
    mouseDownInTable = !!target.closest('td, th');
  });
  document.addEventListener('mouseup', () => {
    mouseDownInTable = true;
  });

  const buttonElements: {
    element: HTMLButtonElement;
    btn: ToolbarButton;
  }[] = [];

  // Create left container for formatting buttons
  const leftContainer = document.createElement('div');
  leftContainer.className = 'toolbar-left';

  // Buttons to put in the right container
  const rightButtonClasses = ['reset-btn', 'qr-btn'];

  for (const btn of buttons) {
    // Skip buttons that go in the right container
    if (rightButtonClasses.includes(btn.className)) continue;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = btn.className;
    button.title = btn.title;
    button.addEventListener('click', (e) => {
      e.preventDefault();
      if (!button.disabled) {
        btn.command(view);
      }
    });
    leftContainer.appendChild(button);
    buttonElements.push({ element: button, btn });
  }

  toolbar.appendChild(leftContainer);

  // Lock mode message (hidden by default)
  const lockMessage = document.createElement('span');
  lockMessage.className = 'lock-message';
  lockMessage.textContent = 'Lock mode active, editing disabled.';
  toolbar.appendChild(lockMessage);

  // Create right container for utility buttons
  const rightContainer = document.createElement('div');
  rightContainer.className = 'toolbar-right';

  // Add reset and qr buttons to right container
  for (const btn of buttons) {
    if (!rightButtonClasses.includes(btn.className)) continue;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = btn.className;
    button.title = btn.title;
    button.addEventListener('click', (e) => {
      e.preventDefault();
      if (!button.disabled) {
        btn.command(view);
      }
    });
    rightContainer.appendChild(button);
    buttonElements.push({ element: button, btn });
  }

  // Add presentation button to right container
  const presentationButton = createPresentationButton();
  rightContainer.appendChild(presentationButton);

  // Add lock button to right container
  const lockButton = createLockButton(view);
  rightContainer.appendChild(lockButton);

  // Add connection indicator to right container
  const indicator = document.createElement('div');
  indicator.className = 'connection-indicator';
  indicator.title = 'Disconnected';
  rightContainer.appendChild(indicator);

  toolbar.appendChild(rightContainer);

  // Update indicator when connection status changes
  sync.onConnectionChange((connected) => {
    indicator.classList.toggle('connected', connected);
    indicator.title = connected ? 'Connected' : 'Disconnected';
  });

  // Create secondary toolbar
  const secondaryToolbar = document.createElement('div');
  secondaryToolbar.className = 'secondary-toolbar';

  // Add always-visible secondary buttons
  const secondaryButtonElements: { element: HTMLButtonElement; btn: ToolbarButton }[] = [];
  for (const btn of secondaryButtons) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = btn.className;
    button.title = btn.title;
    button.addEventListener('click', (e) => {
      e.preventDefault();
      btn.command(view);
    });
    secondaryToolbar.appendChild(button);
    secondaryButtonElements.push({ element: button, btn });
  }

  // Add table-specific buttons (hidden when not in table)
  const tableButtonElements: HTMLButtonElement[] = [];
  for (const btn of tableButtons) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = btn.className;
    button.title = btn.title;
    button.addEventListener('click', (e) => {
      e.preventDefault();
      btn.command(view);
    });
    secondaryToolbar.appendChild(button);
    tableButtonElements.push(button);
  }

  // Update active/disabled states and table button visibility
  const update = () => {
    const locked = isLockedMode(view);

    // Show lock message and hide left container in lock mode
    leftContainer.style.display = locked ? 'none' : '';
    lockMessage.style.display = locked ? 'inline' : 'none';

    for (const { element, btn } of buttonElements) {
      if (btn.isActive) {
        element.classList.toggle('active', btn.isActive(view));
      }
      if (btn.isDisabled) {
        element.disabled = btn.isDisabled(view);
      }
    }
    for (const { element, btn } of secondaryButtonElements) {
      if (btn.isActive) {
        element.classList.toggle('active', btn.isActive(view));
      }
      if (btn.isDisabled) {
        element.disabled = btn.isDisabled(view);
      }
    }
    // Show/hide secondary toolbar (table buttons) based on cursor position
    // Only show if mousedown started inside table (prevents drag-across issues)
    const isInTableCell = mouseDownInTable && inTableCell(view);
    secondaryToolbar.style.display = (locked || !isInTableCell) ? 'none' : 'flex';
  };

  return { element: toolbar, secondaryToolbar, update };
}
